import {
  SubjectAnswers,
  SurveyCakeLogicEngine
} from '@surveycake/logic-engine';
import {
  useSubjectAnswers,
  UseSubjectAnswersSelector
} from '@surveycake/react-logic-engine';
import { useCallback, useEffect, useRef } from 'react';

const answersSelector: UseSubjectAnswersSelector<SubjectAnswers> = answers =>
  answers;

/**
 * The factory to create the hook for binding answer to `input` or `textarea`.
 * The purpose to use uncontrolled component instead of controlled component:
 * 1. Since the hook is complicated and every user input will trigger component to re-render.
 * To avoid from performance issue.
 * 2. The cross browswer issue of `type="number"`.
 * At the first, IE11 or Edge doesn't support the number type on input element.
 * Second, the `e`, Natural logarithm, is also acceptable even though the type set to number.
 * But it will cause the value of input element be empty string if the input value is bad.
 *
 * Please see all hooks created by this factory.
 *
 * @typeparm V - Tyep of value.
 * @typeparam E - HTMLElement type.
 * @param onAnswerChange - While answer from engine changed, will be triggered to change the HTMLElement.
 * @param getPayloadFromElement - To get the payload for `engine.setAnswer` from HTMLElement.
 */
export function createUseInputRefAndSetAnswer<
  V extends string | number,
  E extends HTMLInputElement | HTMLTextAreaElement
>(
  onAnswerChange: (element: E, value?: V) => void,
  getPayloadFromElement: (element: E) => [V] | []
) {
  return function useInputRefAndSetAnswer(
    engine: SurveyCakeLogicEngine,
    id: number
  ) {
    const answers = useSubjectAnswers(id, answersSelector);
    const value = answers.payload[0] as V;
    const inputRef = useRef<E>(null);
    const dirty = useRef(false);
    const onChange = useCallback(() => {
      dirty.current = true;
    }, []);
    const setAnswer = useCallback(async () => {
      if (!inputRef.current || !dirty.current) {
        return;
      }

      try {
        const payload = getPayloadFromElement(inputRef.current);
        await engine.setAnswer(id, { payload });
      } catch (error) {}
    }, [engine, id]);

    useEffect(() => {
      if (!inputRef.current) {
        return;
      }

      onAnswerChange(inputRef.current, value);
      dirty.current = false;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [answers]);

    return { inputRef, onChange, setAnswer };
  };
}
